import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import {
  ArrowLeftRight,
  Book,
  Clock,
  AlertCircle,
  Search,
  UserCheck,
  BookOpen,
} from "lucide-react";

interface DashboardStats {
  circulation: {
    checkoutsToday: number;
    returnsToday: number;
    overdueItems: number;
    activeCheckouts: number;
    pendingReservations: number;
  };
}

export function LibrarianDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const controller = new AbortController();
    const load = async () => {
      const tryLoad = async (): Promise<boolean> => {
        try {
          const res = await api.get("/dashboard/stats", { signal: controller.signal });
          if (!controller.signal.aborted) setStats(res.data);
          return true;
        } catch {
          return false;
        }
      };
      let ok = await tryLoad();
      if (!ok && !controller.signal.aborted) {
        await new Promise((r) => setTimeout(r, 400));
        if (!controller.signal.aborted) await tryLoad();
      }
      if (!controller.signal.aborted) setLoading(false);
    };
    load();
    return () => controller.abort();
  }, []);

  const greeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good morning";
    if (hour < 17) return "Good afternoon";
    return "Good evening";
  };

  if (loading) {
    return <LibrarianSkeleton />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="font-heading text-2xl font-bold text-slate-900">
          {greeting()}
          {user?.firstName ? `, ${user.firstName}` : ""}
        </h1>
        <p className="text-sm text-slate-500 mt-1">Manage circulation and catalog operations</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Checkouts Today"
          value={stats?.circulation?.checkoutsToday ?? 0}
          icon={<ArrowLeftRight className="w-5 h-5" />}
          iconBg="bg-primary/5 text-primary"
        />
        <StatCard
          label="Returns Today"
          value={stats?.circulation?.returnsToday ?? 0}
          icon={<Book className="w-5 h-5" />}
          iconBg="bg-primary/5 text-primary"
        />
        <StatCard
          label="Pending Reservations"
          value={stats?.circulation?.pendingReservations ?? 0}
          icon={<Clock className="w-5 h-5" />}
          iconBg="bg-amber-50 text-amber-600"
        />
        <StatCard
          label="Overdue Items"
          value={stats?.circulation?.overdueItems ?? 0}
          icon={<AlertCircle className="w-5 h-5" />}
          iconBg="bg-red-50 text-red-600"
          valueColor={(stats?.circulation?.overdueItems ?? 0) > 0 ? "text-red-600" : undefined}
        />
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pending Actions */}
        <Card className="border border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-base font-semibold text-slate-900">
              Pending Actions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <PendingItem
                icon={<Clock className="w-4 h-4 text-amber-500" />}
                title="Renewal Requests"
                subtitle="Pending review"
                variant="warning"
                count={stats?.circulation?.pendingReservations ?? 0}
              />
              <PendingItem
                icon={<AlertCircle className="w-4 h-4 text-red-500" />}
                title="Overdue Notices"
                subtitle="Need to be sent"
                variant="danger"
                count={stats?.circulation?.overdueItems ?? 0}
              />
              {(stats?.circulation?.pendingReservations ?? 0) === 0 &&
                (stats?.circulation?.overdueItems ?? 0) === 0 && (
                  <div className="text-center py-4">
                    <UserCheck className="w-8 h-8 mx-auto mb-2 text-emerald-300" />
                    <p className="text-sm font-medium text-emerald-700">All caught up!</p>
                    <p className="text-xs text-slate-500 mt-1">No pending actions at the moment</p>
                  </div>
                )}
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card className="border border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-base font-semibold text-slate-900">
              Quick Actions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-1.5">
              <QuickAction
                icon={<ArrowLeftRight className="w-4 h-4" />}
                label="Check Out Book"
                description="Process a new checkout"
                onClick={() => navigate("/circulation")}
                highlighted
              />
              <QuickAction
                icon={<BookOpen className="w-4 h-4" />}
                label="Return Book"
                description="Process a book return"
                onClick={() => navigate("/circulation")}
              />
              <QuickAction
                icon={<Search className="w-4 h-4" />}
                label="Search Catalog"
                description="Find books in the library"
                onClick={() => navigate("/catalog")}
              />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ─── Sub-components ────────────────────────────────────────────────────

function StatCard({
  label,
  value,
  icon,
  iconBg,
  valueColor,
}: {
  label: string;
  value: number | string;
  icon: React.ReactNode;
  iconBg: string;
  valueColor?: string;
}) {
  return (
    <Card className="border border-slate-200 p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</p>
          <p className={`text-2xl font-bold mt-1 ${valueColor || "text-slate-900"}`}>
            {typeof value === "number" ? value.toLocaleString() : value}
          </p>
        </div>
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${iconBg}`}>
          {icon}
        </div>
      </div>
    </Card>
  );
}

function PendingItem({
  icon,
  title,
  subtitle,
  variant,
  count,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  variant: "danger" | "warning" | "info";
  count: number | string;
}) {
  const colors = {
    danger: "bg-red-50 border-red-100",
    warning: "bg-amber-50 border-amber-100",
    info: "bg-primary/5 border-primary/10",
  };
  const badgeColors = {
    danger: "bg-red-100 text-red-800",
    warning: "bg-amber-100 text-amber-800",
    info: "bg-primary/10 text-primary",
  };

  return (
    <div className={`flex items-center justify-between p-3 rounded-lg border ${colors[variant]}`}>
      <div className="flex items-center gap-3">
        {icon}
        <div>
          <p className="text-sm font-medium text-slate-900">{title}</p>
          <p className="text-xs text-slate-500">{subtitle}</p>
        </div>
      </div>
      <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${badgeColors[variant]}`}>
        {count}
      </span>
    </div>
  );
}

function QuickAction({
  icon,
  label,
  description,
  onClick,
  highlighted,
}: {
  icon: React.ReactNode;
  label: string;
  description: string;
  onClick: () => void;
  highlighted?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-left cursor-pointer group ${
        highlighted ? "bg-primary/5 hover:bg-primary/10" : "hover:bg-slate-50"
      }`}
    >
      <div
        className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${
          highlighted
            ? "bg-primary/10 text-primary"
            : "bg-slate-100 text-slate-600 group-hover:bg-primary/5 group-hover:text-primary"
        }`}
      >
        {icon}
      </div>
      <div className="min-w-0">
        <p className={`text-sm font-medium ${highlighted ? "text-primary" : "text-slate-900"}`}>
          {label}
        </p>
        <p className={`text-xs ${highlighted ? "text-primary" : "text-slate-500"}`}>
          {description}
        </p>
      </div>
    </button>
  );
}

function LibrarianSkeleton() {
  return (
    <div className="space-y-6" role="status" aria-label="Loading dashboard">
      {/* Header skeleton */}
      <div>
        <div className="h-7 w-52 bg-slate-100 rounded animate-pulse" />
        <div className="h-4 w-64 bg-slate-50 rounded animate-pulse mt-2" />
      </div>

      {/* Stats grid skeleton */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="bg-white border border-slate-200 rounded-lg p-5">
            <div className="flex items-start justify-between">
              <div className="space-y-2">
                <div className="h-3 w-24 bg-slate-100 rounded animate-pulse" />
                <div className="h-7 w-10 bg-slate-100 rounded animate-pulse" />
              </div>
              <div className="w-10 h-10 rounded-lg bg-primary/5 animate-pulse" />
            </div>
          </div>
        ))}
      </div>

      {/* Main grid skeleton */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-lg p-5">
          <div className="h-4 w-32 bg-slate-100 rounded animate-pulse mb-4" />
          <div className="space-y-2">
            <div className="h-14 bg-amber-50/50 rounded-lg animate-pulse" />
            <div className="h-14 bg-slate-50 rounded-lg animate-pulse" />
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-5">
          <div className="h-4 w-28 bg-slate-100 rounded animate-pulse mb-3" />
          <div className="space-y-1.5">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="flex items-center gap-3 py-2">
                <div className="w-9 h-9 rounded-lg bg-slate-50 animate-pulse flex-shrink-0" />
                <div className="space-y-1.5 flex-1">
                  <div className="h-3.5 w-28 bg-slate-50 rounded animate-pulse" />
                  <div className="h-2.5 w-36 bg-slate-50/80 rounded animate-pulse" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <span className="sr-only">Loading dashboard statistics...</span>
    </div>
  );
}
